import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { adminApiError, adminApiMessage, evalsApi } from '../adminApi';
import { agentLabel, agentVersions, isExpertRole, runAgents, runVersionsOf } from '../evals/agents';
import { EvalCaseDrawer } from '../components/EvalCaseDrawer';
import {
  CaseMarkerChips,
  CaseOutcome,
  EvalBadge,
  EvalError,
  GateBadge,
  RunLink,
  StatusPill,
} from '../evals/EvalBits';
import { caseRowKey, caseRowTestId, splitLabel, verdictReason } from '../evals/caseTable';
import { EvalRunProgress } from '../evals/EvalProgress';
import { EvalRegradeSection } from '../evals/EvalRegrade';
import {
  formatInterval,
  formatMs,
  formatRate,
  formatResolvable,
  formatUsd,
  gradingLabel,
  metricLabel,
  modelShortName,
  pinBlockText,
  runDemoText,
  selectionText,
  shortId,
  shortSha,
  surfaceLabel,
} from '../evals/format';
import { MIXED_CODE_LABEL, buildText, isLiveRun } from '../evals/progress';
import {
  caseFilterActive,
  caseFilterFromSearch,
  caseFilterSearch,
  compareHref,
  pageOffsets,
} from '../evals/query';
import { regradeOffer } from '../evals/regrade';
import {
  EVAL_METRICS,
  type EvalCaseFilter,
  type EvalCaseList,
  type EvalCaseRow,
  type EvalRole,
  type EvalRunDetail,
  type EvalRunListItem,
  type EvalRunModelDetail,
  type EvalSplitKey,
  type EvalSurface,
} from '../evals/types';
import { GuideLink } from '../evals/GuideLink';
import { formatDate } from '../format';

// One stored eval run (task eval_admin_runs): each model's summary with its
// intervals, flakes, judge flips, reliability and provider mix; its baseline
// state with pin and unpin (audited); and every case, filterable, with a
// drawer that says why a case passed or failed. Test-split cases stay hidden
// until revealed one repeat at a time (EvalCaseDrawer).

const SPLIT_ORDER: EvalSplitKey[] = ['all', 'train', 'test'];
const SURFACES: EvalSurface[] = ['attack', 'helpful', 'question', 'condition'];

function RunMeta({ run, models }: { run: EvalRunDetail['run']; models: EvalRunModelDetail[] }) {
  const selection = selectionText(run);
  // Which agent ran, and each one's own version (TOM-533): an expert's run is
  // the expert alone over its own cases.
  const agents = runAgents(models);
  const expert = agents.length > 0 && agents.every(isExpertRole);
  // An expert's own questions never hold a METS demo case: nothing to say.
  const demo = expert ? null : runDemoText(run.launchParams);
  return (
    <dl className="admin-defs eval-meta" data-testid="eval-run-meta">
      <dt>Agent</dt>
      <dd data-testid="eval-run-agents">
        {agents.length > 0 ? agents.map(agentLabel).join(' and ') : '—'}
        {expert ? (
          <span className="admin-muted"> · run alone over its own questions, without Neo</span>
        ) : null}
      </dd>
      <dt>Suite</dt>
      <dd>
        {run.suite}
        {run.split !== null ? ` · ${run.split} split` : ''} · {run.repeats}{' '}
        {run.repeats === 1 ? 'repeat' : 'repeats'}
      </dd>
      <dt>Selection</dt>
      <dd data-testid="eval-run-selection">
        {selection} ({run.plannedCaseCount} cases){demo !== null ? ` · ${demo}` : ''}
      </dd>
      <dt>Kind</dt>
      <dd>
        {run.runKind}
        {run.gradedFromRunId !== null ? (
          <>
            {' '}
            of <RunLink id={run.gradedFromRunId} />
            {run.gradedFromInStore === false ? ' (not in this store)' : ''}
          </>
        ) : null}
      </dd>
      <dt>Started</dt>
      <dd>
        {formatDate(run.startedAt ?? run.createdAt)}
        {run.finishedAt !== null ? ` → ${formatDate(run.finishedAt)}` : ''}
      </dd>
      <dt>Launched by</dt>
      <dd>
        {run.launchedBy}
        {run.launchedByEmail !== null ? ` · ${run.launchedByEmail}` : ''}
      </dd>
      {run.importedAt !== null ? (
        <>
          <dt>Imported</dt>
          <dd>
            {formatDate(run.importedAt)}
            {run.importedByEmail !== null ? ` by ${run.importedByEmail}` : ''}
          </dd>
        </>
      ) : null}
      <dt>Code</dt>
      <dd data-testid="eval-run-code">
        {run.mixedCode ? (
          <EvalBadge
            tone="warn"
            title="Its shards ran on different code (a deploy landed mid-run)"
            testId="eval-mixed-code"
          >
            {MIXED_CODE_LABEL}
          </EvalBadge>
        ) : run.gitSha === null && run.shardCode.length === 1 ? (
          // A worker run before finalize: its shards have named the build.
          <code>{buildText(run.shardCode[0]!)}</code>
        ) : (
          <>
            <code>{shortSha(run.gitSha)}</code>
            {run.gitDirty === true ? ' (uncommitted changes)' : ''}
          </>
        )}
        {run.mixedCode && run.shardCode.length > 0 ? (
          <ul className="eval-list">
            {run.shardCode.map((c, i) => (
              <li key={i}>
                <code>{buildText(c)}</code>
              </li>
            ))}
          </ul>
        ) : null}
      </dd>
      <dt>{agents.length === 1 ? 'Version' : 'Versions'}</dt>
      <dd data-testid="eval-run-versions">
        {agentVersions(runVersionsOf(run), models).map((a, i) => (
          <span key={a.role}>
            {i > 0 ? ' · ' : ''}
            {a.label} <code>{a.version ?? '—'}</code>
          </span>
        ))}
      </dd>
      <dt>Judges</dt>
      <dd>
        {run.judges.length > 0 ? run.judges.map((j) => <code key={j}>{j} </code>) : 'none'}
        {run.judgeVersion !== null ? (
          <span className="admin-muted"> · {run.judgeVersion}</span>
        ) : null}
        {run.agreementPartial ? (
          <EvalBadge tone="warn" title="A second judge's grading failed for some results">
            Agreement partial
          </EvalBadge>
        ) : null}
      </dd>
      <dt>Cost</dt>
      <dd className="admin-defs-lead">
        {formatUsd(run.totalCostUsd)}
        {run.budgetUsd !== null ? (
          <span className="admin-muted"> of {formatUsd(run.budgetUsd)} budget</span>
        ) : null}
      </dd>
      {run.error !== null ? (
        <>
          <dt>Error</dt>
          <dd className="eval-error-text">{run.error}</dd>
        </>
      ) : null}
      {run.notes !== null ? (
        <>
          <dt>Notes</dt>
          <dd>{run.notes}</dd>
        </>
      ) : null}
    </dl>
  );
}

function Baseline({
  runId,
  m,
  onChanged,
}: {
  runId: string;
  m: EvalRunModelDetail;
  onChanged: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmUnpin, setConfirmUnpin] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  async function pin(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const n = note.trim();
      const res = await evalsApi.pin(runId, {
        model: m.model,
        role: m.role,
        ...(n.length > 0 ? { note: n } : {}),
      });
      setMessage({
        ok: true,
        text:
          res.outcome === 'already_pinned'
            ? 'Already the baseline.'
            : res.replaced !== null
              ? `Pinned. It replaces run ${shortId(res.replaced.runId)}.`
              : 'Pinned.',
      });
      onChanged();
    } catch (err) {
      const body = adminApiError(err);
      const reasons = body.reasons?.map(pinBlockText).join('; ');
      setMessage({
        ok: false,
        text:
          reasons !== undefined && reasons.length > 0
            ? `Not pinnable: ${reasons}.`
            : adminApiMessage(err, 'The pin failed.'),
      });
    } finally {
      setBusy(false);
    }
  }

  async function unpin() {
    setBusy(true);
    setMessage(null);
    try {
      await evalsApi.unpin(runId, { model: m.model, role: m.role });
      setConfirmUnpin(false);
      setMessage({ ok: true, text: 'Unpinned.' });
      onChanged();
    } catch (err) {
      setMessage({ ok: false, text: adminApiMessage(err, 'The unpin failed.') });
    } finally {
      setBusy(false);
    }
  }

  const key = m.pinnable.key;
  return (
    <div className="eval-baseline" data-testid={`eval-baseline-${m.role}-${m.model}`}>
      <h4>Baseline</h4>
      {m.baselines.length > 0 ? (
        <ul className="eval-list">
          {m.baselines.map((b) => (
            <li key={`${b.suite}-${b.split}`}>
              Pinned for the {b.suite} suite{b.suite === 'long' ? ` (${b.split})` : ''},{' '}
              {formatDate(b.pinnedAt)}
              {b.pinnedByEmail !== null ? ` by ${b.pinnedByEmail}` : ''}
              {b.note !== null ? <span className="admin-muted"> · {b.note}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {m.baseline ? (
        confirmUnpin ? (
          <div className="admin-confirm">
            <span className="admin-confirm-prompt">
              Unpin? Compare then has no baseline for this model until another run is pinned.
            </span>
            <button
              type="button"
              className="admin-button-danger"
              disabled={busy}
              onClick={() => void unpin()}
            >
              Unpin
            </button>
            <button
              type="button"
              className="admin-link-button"
              onClick={() => setConfirmUnpin(false)}
            >
              Keep
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="admin-button-secondary"
            onClick={() => setConfirmUnpin(true)}
          >
            Unpin this baseline
          </button>
        )
      ) : m.pinnable.ok ? (
        <form className="eval-pin" onSubmit={(e) => void pin(e)}>
          <input
            type="text"
            value={note}
            maxLength={2000}
            placeholder="Note (optional)"
            onChange={(e) => setNote(e.target.value)}
          />
          <button type="submit" className="admin-button-affirm" disabled={busy}>
            Pin as the {key?.suite ?? ''} baseline
          </button>
        </form>
      ) : (
        <div>
          <button type="button" className="admin-button-secondary" disabled>
            Pin as baseline
          </button>
          <p className="admin-muted eval-sub" data-testid="eval-pin-blocked">
            Not pinnable: {m.pinnable.reasons.map(pinBlockText).join('; ')}.
          </p>
        </div>
      )}
      {message !== null ? (
        <p className={message.ok ? 'eval-ok-text' : 'eval-error-text'} role="status">
          {message.text}
        </p>
      ) : null}
    </div>
  );
}

function ModelSummary({
  runId,
  repeats,
  m,
  onChanged,
  onShowCase,
}: {
  runId: string;
  repeats: number;
  m: EvalRunModelDetail;
  onChanged: () => void;
  onShowCase: (caseId: string) => void;
}) {
  const s = m.summary;
  const rows = EVAL_METRICS.flatMap((metric) =>
    SPLIT_ORDER.flatMap((split) => {
      const f = s.metrics[metric]?.[split];
      return f !== undefined && f.cases > 0 ? [{ metric, split, f }] : [];
    })
  );
  const judges = [m.judgeModel, m.secondJudgeModel].filter((j): j is string => j !== null);
  return (
    <article className="eval-card" data-testid={`eval-model-${m.role}-${m.model}`}>
      <header className="eval-card-header">
        <div>
          <h3>
            <code>{m.model}</code>
            <span className="admin-muted eval-nowrap"> · {agentLabel(m.role)}</span>
          </h3>
          <p className="admin-muted eval-sub">
            {gradingLabel(m.grading)}
            {judges.length > 0 ? ` · ${judges.join(', ')}` : ''}
            {m.summarySource === 'recomputed' ? ' · summary recomputed from the rows' : ''}
          </p>
        </div>
        <GateBadge gate={m.gate} reason={m.gateReason} />
      </header>

      {rows.length === 0 ? (
        <p className="admin-empty">No metric has a counted case yet.</p>
      ) : (
        <div className="scroll-x">
          <table className="admin-table eval-table--compact">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Split</th>
                <th>Met</th>
                <th>Rate</th>
                <th>95% interval</th>
                <th>Resolves</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ metric, split, f }) => (
                <tr key={`${metric}-${split}`}>
                  <td>{metricLabel(metric)}</td>
                  <td>{split}</td>
                  <td>
                    {f.met}/{f.cases}
                  </td>
                  <td>{formatRate(f.rate)}</td>
                  <td>{repeats > 1 && f.ci95 !== null ? formatInterval(f.ci95) : '—'}</td>
                  <td>{formatResolvable(f.resolvable)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="eval-card-grid">
        <dl className="eval-kv">
          <dt>Results</dt>
          <dd>
            {s.counts.complete}/{s.counts.planned} complete
            {s.counts.errored > 0 ? ` · ${s.counts.errored} errored` : ''}
            {s.counts.missing > 0 ? ` · ${s.counts.missing} missing` : ''}
            {s.counts.visibilityBlocked > 0 ? ` · ${s.counts.visibilityBlocked} could not see` : ''}
          </dd>
          <dt>Cost</dt>
          <dd>
            {formatUsd(m.costUsd)} model · {formatUsd(m.judgeCostUsd)} judge · {m.modelCalls} calls
            over {m.turns} turns
          </dd>
          <dt>Latency</dt>
          <dd>
            {m.reliability.latencyMs !== null
              ? `p50 ${formatMs(m.reliability.latencyMs.p50)} · p95 ${formatMs(m.reliability.latencyMs.p95)} (${m.reliability.latencyMs.n} turns)`
              : '—'}
          </dd>
          <dt>Reliability</dt>
          <dd>
            {m.reliability.invalidToolCalls} invalid tool calls · {m.reliability.lengthFinishes}{' '}
            length finishes · {m.reliability.emptyReplies} empty replies
          </dd>
          <dt>Providers</dt>
          <dd>
            {m.providerMix.counts.length === 0
              ? 'none recorded'
              : m.providerMix.counts.map((c) => `${c.provider} ${c.count}`).join(' · ')}
            <span className="admin-muted"> (by {m.providerMix.basis})</span>
          </dd>
          <dt>Judge flips</dt>
          <dd>
            {m.judgeFlips.length === 0
              ? 'not measured'
              : m.judgeFlips
                  .map(
                    (j) =>
                      `${modelShortName(j.judge)} ${j.flips}/${j.pairs}${j.rate !== null ? ` (${formatRate(j.rate)})` : ''}${j.failed > 0 ? `, ${j.failed} failed` : ''}`
                  )
                  .join(' · ')}
          </dd>
          {s.agreement !== undefined ? (
            <>
              <dt>Agreement</dt>
              <dd>
                {s.agreement.recorded}/{s.agreement.planned} second gradings
                {s.agreement.failed > 0 ? ` · ${s.agreement.failed} failed` : ''}
                {s.agreement.partial ? ' · partial' : ''}
              </dd>
            </>
          ) : null}
        </dl>
        <div>
          <h4>Flaky cases</h4>
          {s.flakyCaseIds.length === 0 ? (
            <p className="admin-muted">None: every case&apos;s repeats agree.</p>
          ) : (
            <p className="eval-case-links">
              {s.flakyCaseIds.map((id) => (
                <button
                  key={id}
                  type="button"
                  className="admin-link-button"
                  onClick={() => onShowCase(id)}
                >
                  {id}
                </button>
              ))}
            </p>
          )}
          {s.judgeUnstableCaseIds.length > 0 ? (
            <>
              <h4>Judge-unstable cases</h4>
              <p className="eval-case-links">
                {s.judgeUnstableCaseIds.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className="admin-link-button"
                    onClick={() => onShowCase(id)}
                  >
                    {id}
                  </button>
                ))}
              </p>
            </>
          ) : null}
          <Baseline runId={runId} m={m} onChanged={onChanged} />
        </div>
      </div>
    </article>
  );
}

function CaseFilters({
  filter,
  models,
  suite,
  onChange,
}: {
  filter: EvalCaseFilter;
  models: EvalRunModelDetail[];
  suite: 'short' | 'long';
  onChange: (f: EvalCaseFilter) => void;
}) {
  const [q, setQ] = useState(filter.q ?? '');
  useEffect(() => setQ(filter.q ?? ''), [filter.q]);
  const modelValue =
    filter.model !== undefined ? `${filter.role ?? 'chat'}\u0000${filter.model}` : '';

  function set(next: Partial<EvalCaseFilter>) {
    onChange({ ...filter, ...next, offset: undefined });
  }

  function toggle(key: 'failures' | 'flaky' | 'judgeUnstable' | 'incomplete', on: boolean) {
    set({ [key]: on ? true : undefined });
  }

  return (
    <form
      className="eval-filters"
      aria-label="Filter cases"
      onSubmit={(e) => {
        e.preventDefault();
        const t = q.trim();
        set({ q: t.length > 0 ? t : undefined });
      }}
    >
      {models.length > 1 ? (
        <label>
          Model
          <select
            value={modelValue}
            onChange={(e) => {
              if (e.target.value === '') {
                set({ model: undefined, role: undefined });
                return;
              }
              const [role, model] = e.target.value.split('\u0000') as [EvalRole, string];
              set({ model, role });
            }}
          >
            <option value="">Every model</option>
            {models.map((m) => (
              <option key={`${m.role}|${m.model}`} value={`${m.role}\u0000${m.model}`}>
                {modelShortName(m.model)} · {agentLabel(m.role)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label>
        Kind
        <select
          value={filter.surface ?? ''}
          onChange={(e) =>
            set({ surface: (e.target.value || undefined) as EvalSurface | undefined })
          }
        >
          <option value="">Every kind</option>
          {SURFACES.filter((s) =>
            suite === 'short' ? s === 'attack' || s === 'helpful' : true
          ).map((s) => (
            <option key={s} value={s}>
              {surfaceLabel(s)}
            </option>
          ))}
        </select>
      </label>
      {/* An expert's own cases are split too (TOM-533). */}
      {suite === 'long' || models.some((m) => isExpertRole(m.role)) ? (
        <label>
          Split
          <select
            value={filter.split ?? ''}
            onChange={(e) =>
              set({ split: (e.target.value || undefined) as 'train' | 'test' | undefined })
            }
          >
            <option value="">Both</option>
            <option value="train">Train</option>
            <option value="test">Test</option>
          </select>
        </label>
      ) : null}
      <label className="eval-check">
        <input
          type="checkbox"
          checked={filter.failures === true}
          onChange={(e) => toggle('failures', e.target.checked)}
          data-testid="eval-filter-failures"
        />
        Failures only
      </label>
      <label className="eval-check">
        <input
          type="checkbox"
          checked={filter.flaky === true}
          onChange={(e) => toggle('flaky', e.target.checked)}
        />
        Flaky
      </label>
      <label className="eval-check">
        <input
          type="checkbox"
          checked={filter.judgeUnstable === true}
          onChange={(e) => toggle('judgeUnstable', e.target.checked)}
        />
        Judge unstable
      </label>
      <label className="eval-check">
        <input
          type="checkbox"
          checked={filter.incomplete === true}
          onChange={(e) => toggle('incomplete', e.target.checked)}
        />
        Incomplete
      </label>
      <label>
        Case id
        <input
          type="search"
          value={q}
          maxLength={200}
          placeholder="starts with…"
          onChange={(e) => setQ(e.target.value)}
        />
      </label>
      <button type="submit" className="admin-button-secondary">
        Find
      </button>
    </form>
  );
}

function CaseTable({
  list,
  showModel,
  onOpen,
}: {
  list: EvalCaseList;
  showModel: boolean;
  onOpen: (row: EvalCaseRow) => void;
}) {
  return (
    <div className="scroll-x">
      <table className="admin-table eval-table eval-cases" data-testid="eval-cases-table">
        <thead>
          <tr>
            <th>Case</th>
            {showModel ? <th>Model</th> : null}
            <th>Split</th>
            <th>Outcome</th>
            <th>Markers</th>
            <th>Verdict reason</th>
            <th>Tools</th>
            <th>Cost</th>
            <th>Latency</th>
            <th>Provider</th>
          </tr>
        </thead>
        <tbody>
          {list.cases.map((row) => {
            const reason = verdictReason(row);
            const split = splitLabel(row);
            return (
              <tr key={caseRowKey(row)} data-testid={caseRowTestId(row)}>
                <td>
                  <span className="eval-stack">
                    <button
                      type="button"
                      className="admin-link-button eval-case-open eval-nowrap"
                      onClick={() => onOpen(row)}
                    >
                      {row.caseId}
                    </button>
                    <span className="admin-muted eval-sub">
                      {surfaceLabel(row.surface)}
                      {row.category !== null ? ` · ${row.category}` : ''}
                    </span>
                  </span>
                </td>
                {showModel ? (
                  <td title={row.model} className="eval-nowrap">
                    <code>{modelShortName(row.model)}</code>
                    {row.role !== 'chat' ? (
                      <span className="admin-muted"> {agentLabel(row.role)}</span>
                    ) : null}
                  </td>
                ) : null}
                <td data-testid="eval-case-split">
                  {split ?? <span className="admin-muted">—</span>}
                </td>
                <td>
                  <CaseOutcome row={row} />
                </td>
                <td>
                  <CaseMarkerChips row={row} />
                </td>
                <td className="eval-reason-cell">
                  {row.redacted ? (
                    <span className="eval-hidden-text">Hidden</span>
                  ) : reason !== null ? (
                    <span title={reason}>{reason}</span>
                  ) : (
                    <span className="admin-muted">—</span>
                  )}
                </td>
                <td>
                  {row.tools.length > 0 ? (
                    <span className="eval-stack">
                      {row.tools.map((t) => (
                        <code key={t}>{t}</code>
                      ))}
                    </span>
                  ) : (
                    <span className="admin-muted">none</span>
                  )}
                </td>
                <td>{formatUsd(row.costUsd + row.judgeCostUsd)}</td>
                <td>{formatMs(row.latencyMs)}</td>
                <td>
                  {row.providers.length > 0 ? (
                    row.providers.join(', ')
                  ) : (
                    <span className="admin-muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ComparePicker({ run }: { run: EvalRunDetail['run'] }) {
  const navigate = useNavigate();
  const [others, setOthers] = useState<EvalRunListItem[] | null>(null);
  useEffect(() => {
    let ignore = false;
    evalsApi
      .runs({ suite: run.suite, limit: 50 })
      .then((res) => {
        if (!ignore) setOthers(res.runs.filter((r) => r.id !== run.id));
      })
      .catch(() => {
        if (!ignore) setOthers([]);
      });
    return () => {
      ignore = true;
    };
  }, [run.id, run.suite]);
  return (
    <div className="eval-actions">
      <Link
        className="admin-button-secondary"
        to={compareHref(run.id)}
        data-testid="eval-compare-baseline"
      >
        Compare with the baseline
      </Link>
      <label className="eval-compare-pick">
        <span>Compare with run</span>
        <select
          value=""
          disabled={others === null || others.length === 0}
          data-testid="eval-compare-select"
          onChange={(e) => {
            if (e.target.value !== '') navigate(compareHref(run.id, e.target.value));
          }}
        >
          <option value="">
            {others === null
              ? 'Loading…'
              : others.length === 0
                ? 'No other run of this suite'
                : 'Choose a run…'}
          </option>
          {(others ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {shortId(r.id)} · {formatDate(r.startedAt ?? r.createdAt)} ·{' '}
              {r.models.map((m) => modelShortName(m.model)).join(', ')}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/**
 * Keyed by the run's id, so moving to another run (a re-grade just started,
 * a compare link) starts the page afresh: an old run's details, cases or
 * re-grade form never show under the new run's address (TOM-257).
 */
export function AdminEvalRunDetail() {
  const { id = '' } = useParams();
  return <EvalRunPage key={id} id={id} />;
}

function EvalRunPage({ id }: { id: string }) {
  const [search, setSearch] = useSearchParams();
  const caseFilter = caseFilterFromSearch(search);
  const caseKey = caseFilterSearch(caseFilter);
  const [detail, setDetail] = useState<EvalRunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cases, setCases] = useState<EvalCaseList | null>(null);
  const [casesError, setCasesError] = useState<string | null>(null);
  const [open, setOpen] = useState<EvalCaseRow | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let ignore = false;
    setError(null);
    evalsApi
      .run(id)
      .then((res) => {
        if (!ignore) setDetail(res);
      })
      .catch((err) => {
        if (ignore) return;
        setError(
          (err as { status?: number }).status === 404
            ? `No stored eval run ${id}.`
            : adminApiMessage(err, 'Failed to load this run.')
        );
      });
    return () => {
      ignore = true;
    };
  }, [id, reload]);

  useEffect(() => {
    let ignore = false;
    setCasesError(null);
    evalsApi
      .cases(id, caseFilterFromSearch(new URLSearchParams(caseKey)))
      .then((res) => {
        if (!ignore) setCases(res);
      })
      .catch((err) => {
        if (!ignore) setCasesError(adminApiMessage(err, 'Failed to load the cases.'));
      });
    return () => {
      ignore = true;
    };
  }, [id, caseKey, reload]);

  const closeDrawer = useCallback(() => setOpen(null), []);
  const onChanged = useCallback(() => setReload((n) => n + 1), []);

  function applyCases(next: EvalCaseFilter) {
    setSearch(new URLSearchParams(caseFilterSearch(next)), { replace: true });
  }

  const run = detail?.run;
  const pages = cases !== null ? pageOffsets(cases.total, cases.offset, cases.limit) : null;
  // TOM-257: the re-grade leads the page when the run's results need grading.
  const offer = detail !== null ? regradeOffer(detail) : null;
  const regradeFirst =
    offer !== null && (offer.kind === 'wait' || (offer.kind === 'offer' && offer.needed));
  const regradeSection =
    detail !== null && offer !== null ? (
      <EvalRegradeSection
        key={detail.run.id}
        run={detail.run}
        regrades={detail.regrades}
        offer={offer}
      />
    ) : null;

  return (
    <section className="admin-section eval-page">
      <p className="admin-hint">
        <Link to="/admin/evals/runs">← All runs</Link>
      </p>
      <header className="admin-section-header">
        <h1>
          Run <code title={id}>{shortId(id)}</code>
        </h1>
        {run !== undefined ? (
          <span className="admin-count">
            <StatusPill status={run.status} stalled={run.stalled} />
          </span>
        ) : null}
        <GuideLink section="run-page" />
      </header>

      <EvalError message={error} />
      {detail === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {detail !== null && run !== undefined ? (
        <>
          {/* TOM-120: a live run's progress, polled until it ends, with Cancel. */}
          {isLiveRun(run.status) ? <EvalRunProgress runId={run.id} onEnded={onChanged} /> : null}
          {run.mixedCode ? (
            <p
              className="eval-callout eval-mixed-callout"
              role="note"
              data-testid="eval-mixed-code-callout"
            >
              <strong>{MIXED_CODE_LABEL}.</strong> This run&apos;s shards ran on more than one build
              (a deploy landed mid-run), so its figures are not comparable with another run&apos;s,
              and it can never be a baseline.
            </p>
          ) : null}
          {regradeFirst ? regradeSection : null}
          <RunMeta run={run} models={detail.models} />
          <ComparePicker run={run} />
          {!regradeFirst ? regradeSection : null}

          <h2>Models</h2>
          <div className="eval-cards">
            {detail.models.map((m) => (
              <ModelSummary
                key={`${m.role}|${m.model}`}
                runId={run.id}
                repeats={run.repeats}
                m={m}
                onChanged={onChanged}
                onShowCase={(caseId) => applyCases({ model: m.model, role: m.role, q: caseId })}
              />
            ))}
          </div>

          <h2>
            Cases{' '}
            {cases !== null ? (
              <span className="admin-count">
                {cases.total} {cases.total === 1 ? 'row' : 'rows'}
                {caseFilterActive(caseFilter) ? ' (filtered)' : ''}
              </span>
            ) : null}
          </h2>
          <p className="admin-hint">
            One row per model and case. A case passes on the majority of its repeats; a tie fails.
            Open a case for its prompt, replies, tool trace, judge reasons and rubric.
          </p>
          <CaseFilters
            filter={caseFilter}
            models={detail.models}
            suite={run.suite}
            onChange={applyCases}
          />
          <EvalError message={casesError} />
          {cases === null && casesError === null ? (
            <p className="admin-loading-row">Loading cases…</p>
          ) : null}
          {cases !== null ? (
            cases.cases.length === 0 ? (
              <p className="admin-empty" data-testid="eval-cases-empty">
                {caseFilterActive(caseFilter)
                  ? 'No case matches these filters.'
                  : 'This run has no results yet.'}
              </p>
            ) : (
              <>
                <CaseTable list={cases} showModel={detail.models.length > 1} onOpen={setOpen} />
                {pages !== null && (pages.prev !== null || pages.next !== null) ? (
                  <p className="eval-pager">
                    <button
                      type="button"
                      className="admin-link-button"
                      disabled={pages.prev === null}
                      onClick={() => applyCases({ ...caseFilter, offset: pages.prev ?? undefined })}
                    >
                      ← Previous
                    </button>
                    <span className="admin-muted">
                      {pages.from}–{pages.to} of {cases.total}
                    </span>
                    <button
                      type="button"
                      className="admin-link-button"
                      disabled={pages.next === null}
                      onClick={() => applyCases({ ...caseFilter, offset: pages.next ?? undefined })}
                    >
                      Next →
                    </button>
                  </p>
                ) : null}
              </>
            )
          ) : null}
        </>
      ) : null}

      {open !== null ? <EvalCaseDrawer runId={id} row={open} onClose={closeDrawer} /> : null}
    </section>
  );
}
